import type { ReactNode } from "react";
import { PreferredContactMethod, PreferredContactMethodDisplayNames } from "@remi/enums/preferred-contact-method";
import { formatPhoneNumberForDisplay, PhoneNumberFormat } from "@remi/formatting/phone";
import { RnCopy, RnEnvelope, RnMessage, RnPhone, RnStarFilled, RnUser } from "@remi/icons/next";
import { can } from "@remi/permissions";
import { REMI_INTERNAL_ORG } from "@remi/types/organizations";

import { useUserProfile } from "~/contexts/UserProfileContext";
import { usePersonTelecomPhoneNumber } from "~/hooks/phone/usePersonTelecomPhoneNumber";
import { usePerson } from "~/hooks/usePerson";
import { CallButton } from "./components/CallButton";
import { ContactHeader } from "./components/ContactHeader";
import { CopyButton } from "./components/CopyButton";
import { EmailButton } from "./components/EmailButton";
import { PersonNoteSection } from "./components/PersonNoteSection";
import { TextButton } from "./components/TextButton";

type RemiPersonCardProps = {
  personId: string;
  showButtons?: boolean;
};

const ContactLine = ({
  icon,
  label,
  value,
  badge,
  children,
}: {
  icon: ReactNode;
  label?: string;
  value: string;
  badge?: ReactNode;
  children?: ReactNode;
}) => (
  <div className="group flex h-9 w-full items-center gap-2 rounded-lg px-2 hover:bg-background-primaryHover">
    <span aria-hidden className="flex size-4 shrink-0 items-center justify-center text-icon-secondary">
      {icon}
    </span>
    <span title={value} className="type-xs min-w-0 truncate text-text-primary">
      {label !== undefined && <span className="text-text-tertiary">{label}</span>}
      <span className="select-text">{value}</span>
    </span>
    {badge}
    <span className="ml-auto flex items-center gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100">
      {children}
    </span>
  </div>
);

const PreferredBadge = ({ method }: { method: PreferredContactMethod }) => (
  <span className="flex shrink-0 items-center gap-1">
    <RnStarFilled aria-hidden className="text-icon-yellow" />
    <span className="font-medium text-text-primary text-xs">{PreferredContactMethodDisplayNames[method]}</span>
  </span>
);

export const RemiPersonCard = ({ personId, showButtons = true }: RemiPersonCardProps) => {
  const { user } = useUserProfile();
  const { data: person } = usePerson(personId);
  const { data: openPhoneNumber } = usePersonTelecomPhoneNumber(personId);

  const formatNumber = (num: string) => formatPhoneNumberForDisplay(num, { format: PhoneNumberFormat.NATIONAL });
  const canText = can.person.viewDetails(user);
  const preferred = person?.contact.preferredContactMethod ?? undefined;
  const isRemiInternalViewer = user.person.organizationId === REMI_INTERNAL_ORG.id;

  // The OpenPhone line is also in person.phones; drop it here once the telecom query resolves so it only renders as the Quo row.
  const personalPhones = person
    ? [
        ...(person.primaryPhone
          ? [{ id: person.primaryPhone.id, number: person.primaryPhone.number, isPrimary: true }]
          : []),
        ...person.phones
          .filter((p) => p.id !== person.primaryPhoneId)
          .map((p) => ({ id: p.id, number: p.number, isPrimary: false })),
      ].filter((p) => !openPhoneNumber || p.number !== openPhoneNumber)
    : [];

  const formattedOpenPhone = openPhoneNumber ? formatNumber(openPhoneNumber) : undefined;

  const emails = person
    ? [
        ...(person.primaryEmailAddress
          ? [{ id: person.primaryEmailAddress.id, email: person.primaryEmailAddress.emailAddress, isPrimary: true }]
          : []),
        ...person.emailAddresses
          .filter((e) => e.id !== person.primaryEmailAddressId)
          .map((e) => ({ id: e.id, email: e.emailAddress, isPrimary: false })),
      ]
    : [];

  return (
    <>
      <ContactHeader personId={personId} showButtons={showButtons} />
      {person && (
        <div className="flex w-full flex-col px-2 pb-3">
          {personalPhones.map((phone) => {
            const formattedNumber = formatNumber(phone.number);

            return (
              <ContactLine
                key={phone.id}
                icon={<RnPhone />}
                value={formattedNumber}
                badge={
                  phone.isPrimary &&
                  (preferred === PreferredContactMethod.TEXT || preferred === PreferredContactMethod.CALL) ? (
                    <PreferredBadge method={preferred} />
                  ) : undefined
                }
              >
                {showButtons && canText && (
                  <TextButton muted icon={<RnMessage />} personId={personId} number={phone.number} />
                )}
                {showButtons && <CallButton muted icon={<RnPhone />} number={phone.number} />}
                {showButtons && <CopyButton muted icon={<RnCopy />} value={phone.number} valueType="phone" />}
              </ContactLine>
            );
          })}
          {openPhoneNumber && formattedOpenPhone && (
            // Generic phone icon until the Quo brand logo is added to @remi/icons (DES-2812 follow-up).
            <ContactLine icon={<RnPhone />} value={formattedOpenPhone}>
              {showButtons && <CallButton muted icon={<RnPhone />} number={openPhoneNumber} />}
              {showButtons && <CopyButton muted icon={<RnCopy />} value={openPhoneNumber} valueType="phone" />}
            </ContactLine>
          )}
          {emails.map((email) => (
            <ContactLine
              key={email.id}
              icon={<RnEnvelope />}
              value={email.email}
              badge={
                email.isPrimary && preferred === PreferredContactMethod.EMAIL ? (
                  <PreferredBadge method={preferred} />
                ) : undefined
              }
            >
              {showButtons && <EmailButton muted icon={<RnEnvelope />} email={email.email} />}
              {showButtons && <CopyButton muted icon={<RnCopy />} value={email.email} valueType="email" />}
            </ContactLine>
          ))}
          {person.supervisorName !== undefined && (
            <ContactLine icon={<RnUser />} label="Supervisor: " value={person.supervisorName} />
          )}
        </div>
      )}
      {isRemiInternalViewer && <PersonNoteSection personId={personId} />}
    </>
  );
};
